import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withModifiers as _withModifiers, withKeys as _withKeys, withDirectives as _withDirectives, Transition as _Transition } from "vue"
import _imports_0 from '@/public/assets/images/aquassist-icon.svg'


const _hoisted_1 = { class: "aq-chatbot" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = {
  key: 0,
  src: _imports_0,
  alt: "Aquassist",
  class: "aq-chatbot__fab-icon"
}
const _hoisted_4 = {
  key: 2,
  class: "aq-chatbot__fab-badge"
}
const _hoisted_5 = {
  key: 0,
  class: "aq-chatbot__window"
}
const _hoisted_6 = { class: "aq-chatbot__header" }
const _hoisted_7 = { class: "aq-chatbot__header-left" }
const _hoisted_8 = { class: "aq-chatbot__header-info" }
const _hoisted_9 = { class: "aq-chatbot__header-subtitle" }
const _hoisted_10 = { class: "aq-chatbot__header-pill" }
const _hoisted_11 = { class: "aq-chatbot__beta" }
const _hoisted_12 = { class: "aq-chatbot__mode-toggle" }
const _hoisted_13 = { class: "aq-chatbot__message aq-chatbot__message--bot" }
const _hoisted_14 = { class: "aq-chatbot__message-content" }
const _hoisted_15 = { class: "aq-chatbot__message-bubble aq-chatbot__message-bubble--bot" }
const _hoisted_16 = {
  key: 0,
  src: _imports_0,
  alt: "AI",
  class: "aq-chatbot__message-avatar"
}
const _hoisted_17 = { class: "aq-chatbot__message-content" }
const _hoisted_18 = {
  key: 0,
  class: "aq-chatbot__message-name"
}
const _hoisted_19 = {
  key: 0,
  class: "aq-chatbot__insight-label"
}
const _hoisted_20 = {
  key: 1,
  class: "aq-chatbot__sources"
}
const _hoisted_21 = { class: "aq-chatbot__sources-title" }
const _hoisted_22 = {
  key: 0,
  class: "aq-chatbot__message aq-chatbot__message--bot"
}
const _hoisted_23 = { class: "aq-chatbot__input-area" }
const _hoisted_24 = ["placeholder", "disabled", "onKeydown"]
const _hoisted_25 = ["disabled"]

import { ref, nextTick, watch, onMounted } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { getSocketClient } from '@/utilities/socket';

    interface ChatSource {
        name: string;
        uri: string;
    }

    interface ChatMessage {
        role: 'user' | 'bot';
        text: string;
        sources?: ChatSource[];
        isInsight?: boolean;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AQChatbot',
  setup(__props) {

    const { t } = useI18n();

    const isOpen = ref(false);
    const inputText = ref('');
    const isLoading = ref(false);
    const messages = ref<ChatMessage[]>([]);
    const sessionId = ref<string | null>(null);
    const messagesContainer = ref<HTMLElement | null>(null);
    const chatInput = ref<HTMLInputElement | null>(null);
    const chatMode = ref<'kb' | 'standard'>('kb');
    const hasInsight = ref(false);

    const scrollToBottom = async () => {
        await nextTick();
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    };

    const toggleChat = () => {
        isOpen.value = !isOpen.value;

        if (!isOpen.value) {
            sessionId.value = null;
        }

        if (isOpen.value) {
            hasInsight.value = false;
            nextTick(() => {
                chatInput.value?.focus();
                scrollToBottom();
            });
        }
    };

    const sendMessage = () => {
        const text = inputText.value.trim();
        if (!text || isLoading.value) return;

        messages.value.push({ role: 'user', text });
        inputText.value = '';
        isLoading.value = true;
        scrollToBottom();

        const socket = getSocketClient();
        if (!socket) {
            messages.value.push({
                role: 'bot',
                text: t('chatbot.connectionError'),
            });
            isLoading.value = false;
            scrollToBottom();
            return;
        }

        if (chatMode.value === 'kb') {
            socket.emit('GPT_CHAT_MESSAGE', {
                question: text,
                sessionId: sessionId.value,
            });
        } else {
            socket.emit('GPT_DIRECT_MESSAGE', {
                question: text,
            });
        }
    };

    const setupSocketListeners = () => {
        const socket = getSocketClient();
        if (!socket) return;

        socket.on(
            'GPT_CHAT_RESPONSE',
            (data: { text: string; sessionId: string; sources?: ChatSource[]; done: boolean }) => {
                isLoading.value = false;
                if (data.sessionId) {
                    sessionId.value = data.sessionId;
                }
                messages.value.push({
                    role: 'bot',
                    text: data.text,
                    sources: data.sources?.length ? data.sources : undefined,
                });
                scrollToBottom();
            },
        );

        socket.on('GPT_CHAT_ERROR', (data: { error: string }) => {
            isLoading.value = false;

            const errMsg = data.error || t('chatbot.genericError');

            if (errMsg.includes('Knowledge base configurations cannot be modified')) {
                sessionId.value = null;
            }

            messages.value.push({ role: 'bot', text: errMsg });
            scrollToBottom();
        });
    };

    const setupInsightListener = () => {
        const socket = getSocketClient();
        if (!socket) return;

        socket.on('GPT_INSIGHT', (data: { text: string; context: string }) => {
            hasInsight.value = true;
            messages.value.push({
                role: 'bot',
                text: data.text,
                isInsight: true,
            });
            if (isOpen.value) {
                hasInsight.value = false;
                scrollToBottom();
            }
        });
    };

    onMounted(() => {
        setupInsightListener();
    });

    watch(
        isOpen,
        (newVal) => {
            if (newVal) {
                setupSocketListeners();
            }
        },
        { once: true },
    );

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("button", {
      class: _normalizeClass(["aq-chatbot__fab", { 'aq-chatbot__fab--open': isOpen.value }]),
      onClick: toggleChat,
      "aria-label": isOpen.value ? _unref(t)('chatbot.close') : _unref(t)('chatbot.open')
    }, [
      (!isOpen.value)
        ? (_openBlock(), _createElementBlock("img", _hoisted_3))
        : (_openBlock(), _createBlock(_component_v_icon, {
            key: 1,
            color: "white",
            size: "28"
          }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("mdi-close", -1)
            ]))]),
            _: 1
          })),
      (hasInsight.value && !isOpen.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
            _createVNode(_component_v_icon, {
              size: "16",
              color: "#fff"
            }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("mdi-lightbulb-on", -1)
              ]))]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ], 10, _hoisted_2),
    _createVNode(_Transition, { name: "aq-chatbot-slide" }, {
      default: _withCtx(() => [
        (isOpen.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createElementVNode("img", {
                    src: _imports_0,
                    alt: "Aquassist",
                    class: "aq-chatbot__header-logo"
                  }, null, -1)),
                  _createElementVNode("div", _hoisted_8, [
                    _cache[5] || (_cache[5] = _createElementVNode("span", { class: "aq-chatbot__header-title" }, "Aquassist GPT", -1)),
                    _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('chatbot.replyMessage')), 1)
                  ])
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createVNode(_component_v_icon, {
                    size: "14",
                    color: "white"
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode("mdi-chat-processing-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _cache[8] || (_cache[8] = _createElementVNode("span", null, "Chat", -1))
                ]),
                _createElementVNode("button", {
                  class: "aq-chatbot__header-close",
                  onClick: toggleChat
                }, [
                  _createVNode(_component_v_icon, {
                    color: "white",
                    size: "18"
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode("mdi-minus", -1)
                    ]))]),
                    _: 1
                  })
                ])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_component_v_icon, {
                  size: "14",
                  color: "#013067"
                }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("mdi-information-outline", -1)
                  ]))]),
                  _: 1
                }),
                _createElementVNode("span", null, _toDisplayString(_unref(t)('chatbot.betaMessage')), 1)
              ]),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("button", {
                  class: _normalizeClass(["aq-chatbot__mode-btn", { 'aq-chatbot__mode-btn--active': chatMode.value === 'kb' }]),
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (chatMode.value = 'kb'))
                }, [
                  _createVNode(_component_v_icon, { size: "14" }, {
                    default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                      _createTextVNode("mdi-book-search-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _createTextVNode(" " + _toDisplayString(_unref(t)('chatbot.modeKB')), 1)
                ], 2),
                _createElementVNode("button", {
                  class: _normalizeClass(["aq-chatbot__mode-btn", { 'aq-chatbot__mode-btn--active': chatMode.value === 'standard' }]),
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (chatMode.value = 'standard'))
                }, [
                  _createVNode(_component_v_icon, { size: "14" }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode("mdi-robot-outline", -1)
                    ]))]),
                    _: 1
                  }),
                  _createTextVNode(" " + _toDisplayString(_unref(t)('chatbot.modeStandard')), 1)
                ], 2)
              ]),
              _createElementVNode("div", {
                ref_key: "messagesContainer",
                ref: messagesContainer,
                class: "aq-chatbot__messages"
              }, [
                _createElementVNode("div", _hoisted_13, [
                  _cache[14] || (_cache[14] = _createElementVNode("img", {
                    src: _imports_0,
                    alt: "AI",
                    class: "aq-chatbot__message-avatar"
                  }, null, -1)),
                  _createElementVNode("div", _hoisted_14, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "aq-chatbot__message-name" }, "Aquassist support", -1)),
                    _createElementVNode("div", _hoisted_15, _toDisplayString(_unref(t)('chatbot.initialMessage')), 1)
                  ])
                ]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (msg, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: _normalizeClass(["aq-chatbot__message", {
                                'aq-chatbot__message--user': msg.role === 'user',
                                'aq-chatbot__message--bot': msg.role === 'bot',
                            }])
                  }, [
                    (msg.role === 'bot')
                      ? (_openBlock(), _createElementBlock("img", _hoisted_16))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_17, [
                      (msg.role === 'bot')
                        ? (_openBlock(), _createElementBlock("span", _hoisted_18, "Aquassist support"))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", {
                        class: _normalizeClass(["aq-chatbot__message-bubble", {
                                        'aq-chatbot__message-bubble--user': msg.role === 'user',
                                        'aq-chatbot__message-bubble--bot': msg.role === 'bot',
                                        'aq-chatbot__message-bubble--insight':
                                            msg.role === 'bot' && msg.isInsight,
                                    }])
                      }, [
                        (msg.isInsight)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                              _createVNode(_component_v_icon, {
                                size: "14",
                                color: "#f9a825"
                              }, {
                                default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                  _createTextVNode("mdi-lightbulb-on", -1)
                                ]))]),
                                _: 1
                              }),
                              _createTextVNode(" " + _toDisplayString(_unref(t)('chatbot.insightLabel')), 1)
                            ]))
                          : _createCommentVNode("", true),
                        _createTextVNode(" " + _toDisplayString(msg.text) + " ", 1),
                        (msg.sources && msg.sources.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                              _createElementVNode("div", _hoisted_21, [
                                _createVNode(_component_v_icon, {
                                  size: "12",
                                  color: "#2b3b4f"
                                }, {
                                  default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                    _createTextVNode("mdi-file-document-outline", -1)
                                  ]))]),
                                  _: 1
                                }),
                                _createTextVNode(" " + _toDisplayString(_unref(t)('chatbot.sources')), 1)
                              ]),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(msg.sources, (src, sIdx) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: sIdx,
                                  class: "aq-chatbot__source-item"
                                }, _toDisplayString(src.name), 1))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true)
                      ], 2)
                    ])
                  ], 2))
                }), 128)),
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_22, [...(_cache[17] || (_cache[17] = [
                      _createElementVNode("img", {
                        src: _imports_0,
                        alt: "AI",
                        class: "aq-chatbot__message-avatar"
                      }, null, -1),
                      _createElementVNode("div", { class: "aq-chatbot__message-content" }, [
                        _createElementVNode("span", { class: "aq-chatbot__message-name" }, "Aquassist support"),
                        _createElementVNode("div", { class: "aq-chatbot__message-bubble aq-chatbot__message-bubble--bot" }, [
                          _createElementVNode("div", { class: "aq-chatbot__typing" }, [
                            _createElementVNode("span"),
                            _createElementVNode("span"),
                            _createElementVNode("span")
                          ])
                        ])
                      ], -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ], 512),
              _createElementVNode("div", _hoisted_23, [
                _withDirectives(_createElementVNode("input", {
                  ref_key: "chatInput",
                  ref: chatInput,
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((inputText).value = $event)),
                  class: "aq-chatbot__input",
                  placeholder: _unref(t)('chatbot.typeMessage'),
                  disabled: isLoading.value,
                  onKeydown: _withKeys(_withModifiers(sendMessage, ["prevent"]), ["enter"])
                }, null, 40, _hoisted_24), [
                  [_vModelText, inputText.value]
                ]),
                _createElementVNode("button", {
                  class: "aq-chatbot__send",
                  disabled: !inputText.value.trim() || isLoading.value,
                  onClick: sendMessage
                }, [
                  _createVNode(_component_v_icon, {
                    color: inputText.value.trim() && !isLoading.value ? '#013067' : '#9E9E9E',
                    size: "22"
                  }, {
                    default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                      _createTextVNode("mdi-send", -1)
                    ]))]),
                    _: 1
                  }, 8, ["color"])
                ], 8, _hoisted_25)
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})